import { MOOD_WORDS, moodLabel, signedFigure, type DailyStats, type TrendPoints } from '@adhd/shared';
import { bandOf } from '../lib/band.js';

/**
 * The record, drawn as points. Release 6b of 2.0 (docs/PRD-2.0.md §6, and
 * docs/pitch-2026-10-12/mocks/insights.html, the drawing these are built to).
 *
 * Four rules, from the drawing, and every plate here keeps them:
 *   - every chart is POINTS, not summaries, so a thin month looks thin
 *     instead of being hidden or refused;
 *   - every chart carries its n in the mono line under its title;
 *   - nothing is ordered by effect;
 *   - no mean faces the reader.
 *
 * Colour comes only from the seven condition tokens (`--c-awful` …
 * `--c-great`, lib/band.ts), so a day is the same colour here as on Days.
 */

export interface DayCell {
  day: string;
  /** Where the day ended: the last check-in's mood, or null. */
  end: number | null;
  lowest: number | null;
  highest: number | null;
  checkins: number;
  wrote: boolean;
  helpNow: boolean;
  doseSkipped: boolean;
  /** Nothing at all logged. */
  empty: boolean;
}

/** One cell per day from the window's daily stats and its check-in points. */
export function dayCells(daily: DailyStats[], checkins: TrendPoints['checkins'], doseGapDays: Set<string>): DayCell[] {
  const byDay = new Map<string, TrendPoints['checkins']>();
  for (const c of checkins) byDay.set(c.day, [...(byDay.get(c.day) ?? []), c]);
  return daily.map((d) => {
    const cs = (byDay.get(d.day) ?? []).slice().sort((a, b) => a.at.localeCompare(b.at));
    const moods = cs.map((c) => c.mood);
    return {
      day: d.day,
      end: cs.length ? cs[cs.length - 1]!.mood : null,
      lowest: moods.length ? Math.min(...moods) : null,
      highest: moods.length ? Math.max(...moods) : null,
      checkins: cs.length,
      wrote: d.voiceLogs > 0 || d.thoughtRecords > 0,
      helpNow: d.sosEvents > 0,
      doseSkipped: doseGapDays.has(d.day),
      empty: !d.isActive && cs.length === 0,
    };
  });
}

const CELL = 39;
const GAP = 3;
const LEFT = 46;
const TOP = 18;

/* --- The record: every day as a cell ------------------------------------ */

export function RecordGrid({ cells }: { cells: DayCell[] }): JSX.Element {
  if (cells.length === 0) return <figure className="chart-frame plate"><figcaption>Nothing logged yet.</figcaption></figure>;
  // Monday-first rows; the first row is padded back to its Monday.
  const first = cells[0]!.day;
  const lead = (new Date(`${first}T12:00:00Z`).getUTCDay() + 6) % 7;
  const rows: (DayCell | null)[][] = [];
  let row: (DayCell | null)[] = Array(lead).fill(null);
  for (const c of cells) {
    row.push(c);
    if (row.length === 7) { rows.push(row); row = []; }
  }
  if (row.length) { while (row.length < 7) row.push(null); rows.push(row); }
  const w = LEFT + 7 * CELL + 6 * GAP;
  const h = TOP + rows.length * (CELL + GAP);
  const rated = cells.filter((c) => c.end !== null).length;
  return (
    <figure className="chart-frame plate">
      <svg className="chart rec" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`Every day since ${longDay(first)} as a cell, coloured by where it ended; ${rated} of ${cells.length} rated`}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <text key={i} x={LEFT + i * (CELL + GAP) + CELL / 2} y={11} className="rec-ax" textAnchor="middle">{d}</text>
        ))}
        {rows.map((r, ri) => {
          const y = TOP + ri * (CELL + GAP);
          const label = r.find((c) => c !== null)?.day ?? null;
          return (
            <g key={ri}>
              {label && ri % 2 === 0 && <text x={LEFT - 8} y={y + CELL / 2 + 3} className="rec-ax" textAnchor="end">{shortDay(label)}</text>}
              {r.map((c, ci) => {
                if (!c) return null;
                const x = LEFT + ci * (CELL + GAP);
                const title = `${longDay(c.day)}${c.end === null ? (c.empty ? ' · nothing logged' : ' · no check-in') : ` · ${moodLabel(c.end)} ${signedFigure(c.end)} · ${c.checkins} check-in${c.checkins === 1 ? '' : 's'}`}${c.wrote ? ' · wrote' : ''}${c.helpNow ? ' · Help now' : ''}${c.doseSkipped ? ' · dose skipped' : ''}`;
                return (
                  <g key={c.day}>
                    <rect x={x} y={y} width={CELL} height={CELL} rx={3} className={c.end === null ? (c.empty ? 'rec-empty' : 'rec-quiet') : `rec-${bandOf(c.end)}`}>
                      <title>{title}</title>
                    </rect>
                    {c.checkins > 1 && c.lowest !== null && c.highest !== null && c.lowest !== c.highest && (
                      <line x1={x + CELL / 2} x2={x + CELL / 2} y1={y + 4 + ((5 - c.highest) / 10) * (CELL - 8)} y2={y + 4 + ((5 - c.lowest) / 10) * (CELL - 8)} className="rec-range" />
                    )}
                    {c.wrote && <circle cx={x + CELL - 6} cy={y + 6} r={2.4} className="rec-dot" />}
                    {c.helpNow && <path d={`M${x + 5} ${y + CELL - 5} l3.5 -4 l3.5 4`} className="rec-sos" />}
                    {c.doseSkipped && <rect x={x + CELL - 10} y={y + CELL - 10} width={5} height={5} className="rec-dose" />}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="rec-legend" aria-hidden="true">
        {(['awful', 'low', 'neutral', 'alright', 'great'] as const).map((b) => (
          <span key={b}><i className={`rec-sw rec-${b}`} />{{ awful: 'Rough / Awful', low: 'A bit low', neutral: 'Neutral', alright: 'Alright', great: 'Good / Great' }[b]}</span>
        ))}
        <span><i className="rec-sw rec-quiet" />No check-in</span>
        <span><i className="rec-sw rec-sw-range" />Range, on a day with more than one</span>
        <span><i className="rec-sw rec-sw-dot" />Wrote</span>
        <span><i className="rec-sw rec-sw-sos" />Opened Help now</span>
        <span><i className="rec-sw rec-sw-dose" />Dose skipped</span>
      </div>
      <figcaption>
        Every day since you started, as a cell: where it ended, how far it ranged, whether you wrote, whether you opened Help now, whether a scheduled dose went unmarked. A blank day is drawn blank — a gap is a fact about the record, not a crash.{' '}
        <span className="n">{rated} of {cells.length} days rated</span>.
      </figcaption>
    </figure>
  );
}

/* --- Thread lanes: what was running, across the window -------------------- */

export function ThreadLanes({ events, from, to }: { events: { label: string; startedOn: string; endedOn: string | null }[]; from: string; to: string }): JSX.Element | null {
  if (events.length === 0) return null;
  const days = daysBetween(from, to) + 1;
  const w = 343;
  const lane = 18;
  const h = events.length * lane + 24;
  const x = (day: string): number => (Math.max(0, Math.min(days - 1, daysBetween(from, day))) / Math.max(1, days - 1)) * (w - 2) + 1;
  return (
    <svg className="chart lanes" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${events.length} thread${events.length === 1 ? '' : 's'} across the window`}>
      <text x={0} y={9} className="rec-ax">{shortDay(from)}</text>
      <text x={w} y={9} className="rec-ax" textAnchor="end">{shortDay(to)}</text>
      {events.map((e, i) => {
        const x1 = x(e.startedOn < from ? from : e.startedOn);
        const x2 = x(e.endedOn && e.endedOn < to ? e.endedOn : to);
        const y = 24 + i * lane;
        return (
          <g key={`${e.label}-${e.startedOn}`}>
            <rect x={x1} y={y + 3} width={Math.max(2, x2 - x1)} height={6} rx={3} className="lane-bar" />
            <text x={Math.min(x1, w - 80)} y={y + 1} className="rec-ax lane-lbl">{e.label || 'Something big'}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* --- Inside a day: from the first check-in to the last ------------------- */

export function InsideDay({ cells, checkins }: { cells: DayCell[]; checkins: TrendPoints['checkins'] }): JSX.Element {
  const byDay = new Map<string, number[]>();
  for (const c of checkins.slice().sort((a, b) => a.at.localeCompare(b.at))) byDay.set(c.day, [...(byDay.get(c.day) ?? []), c.mood]);
  const lines = [...byDay.entries()].filter(([, m]) => m.length >= 2).map(([day, m]) => ({ day, first: m[0]!, last: m[m.length - 1]! }));
  const up = lines.filter((l) => l.last > l.first).length;
  const down = lines.filter((l) => l.last < l.first).length;
  const same = lines.length - up - down;
  const w = 343, h = 150, x1 = 60, x2 = 283;
  const y = (m: number): number => 10 + ((5 - m) / 10) * 118;
  return (
    <figure className="chart-frame plate">
      {lines.length === 0 ? (
        <figcaption>No day in this window has two check-ins yet. A second check-in on a day is what draws a line here. <span className="n">0 days</span>.</figcaption>
      ) : (
        <>
          <svg className="chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${lines.length} days with two or more check-ins, each a line from its first to its last`}>
            {[5, 0, -5].map((m) => (
              <g key={m}>
                <line x1={x1} x2={x2} y1={y(m)} y2={y(m)} className={m === 0 ? 'rec-axis' : 'rec-grid'} />
                <text x={x1 - 6} y={y(m) + 4} className="rec-ax" textAnchor="end">{m === 0 ? '0' : signedFigure(m)}</text>
              </g>
            ))}
            <text x={x1} y={146} className="rec-ax">first check-in</text>
            <text x={x2} y={146} className="rec-ax" textAnchor="end">last check-in</text>
            {lines.map((l) => (
              <line key={l.day} x1={x1} x2={x2} y1={y(l.first)} y2={y(l.last)} className={l.last > l.first ? 'slope slope-up' : l.last < l.first ? 'slope slope-down' : 'slope slope-same'}>
                <title>{`${longDay(l.day)}: ${signedFigure(l.first)} → ${signedFigure(l.last)}`}</title>
              </line>
            ))}
          </svg>
          <figcaption>
            Each line is one day, from its first check-in to its last. {down} went down, {up} went up, {same} stayed. Drawn like this nobody has to be told whether there is a pattern; it is there to see the day one appears.{' '}
            <span className="n">{lines.length} of {cells.filter((c) => c.checkins > 0).length} rated days with two or more check-ins</span>.
          </figcaption>
        </>
      )}
    </figure>
  );
}

/* --- The weeks: one row per week, one dot per rated day ------------------ */

const SCALE_LEFT = 56;
const SCALE_W = 280;

/** x for a mood on the seven bands at their real, unequal widths. */
function scaleX(mood: number): number {
  return SCALE_LEFT + ((mood + 5) / 10) * SCALE_W;
}

function BandAxis({ y }: { y: number }): JSX.Element {
  return (
    <>
      {MOOD_WORDS.map((b) => {
        const x1 = scaleX(b.lo - 0.5) + 1, x2 = scaleX(b.hi + 0.5) - 1;
        return (
          <g key={b.word}>
            <line x1={x1} x2={x2} y1={y} y2={y} className="rec-axis" />
            {b.width > 1 && <text x={(x1 + x2) / 2} y={y - 3} className="rec-ax rec-ax-sm" textAnchor="middle">{b.word}</text>}
          </g>
        );
      })}
    </>
  );
}

export function WeeksPlate({ cells }: { cells: DayCell[] }): JSX.Element {
  const weeks = new Map<string, DayCell[]>();
  for (const c of cells) {
    const k = mondayOf(c.day);
    weeks.set(k, [...(weeks.get(k) ?? []), c]);
  }
  const rows = [...weeks.entries()];
  if (rows.length === 0) return <figure className="chart-frame plate"><figcaption>Nothing logged yet.</figcaption></figure>;
  const rowH = 24;
  const w = 343, h = 22 + rows.length * rowH + 6;
  return (
    <figure className="chart-frame plate">
      <svg className="chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${rows.length} weeks, one dot per rated day at where it ended`}>
        <BandAxis y={8} />
        {Array.from({ length: 11 }, (_, i) => i - 5).map((m) => (
          <line key={m} x1={scaleX(m)} x2={scaleX(m)} y1={22} y2={h - 4} className={m === 0 ? 'rec-axis' : 'rec-grid'} />
        ))}
        {rows.map(([monday, days], i) => {
          const y = 22 + i * rowH + rowH / 2;
          const rated = days.filter((d) => d.end !== null);
          return (
            <g key={monday}>
              <text x={SCALE_LEFT - 8} y={y + 3} className="rec-ax" textAnchor="end">{shortDay(monday)}</text>
              {rated.map((d) => (
                <circle key={d.day} cx={scaleX(d.end!) + jitter(d.day)} cy={y} r={4} className={`pt rec-${bandOf(d.end!)}`}>
                  <title>{`${longDay(d.day)} · ${moodLabel(d.end!)} ${signedFigure(d.end!)}`}</title>
                </circle>
              ))}
              <text x={w} y={y + 3} className="rec-ax" textAnchor="end">{rated.length}/{days.length}</text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        One row per week, one dot per day at where it ended, on the seven bands at their real and unequal widths. No average, no line through the middle. The right-hand figure is rated days over days in the week.{' '}
        <span className="n">{rows.length} weeks · {cells.filter((c) => c.end !== null).length} rated days</span>.
      </figcaption>
    </figure>
  );
}

/* --- The marks: with and without, as points on one scale ------------------ */

export function MarksLedger({ cells, marks, since }: { cells: DayCell[]; marks: TrendPoints['marks']; since: string | null }): JSX.Element {
  // The "without" side counts only from the day marks began: before that, an
  // unmarked day is a day that could not have been marked.
  const rated = cells.filter((c) => c.end !== null && (since === null || c.day >= since));
  const ledger = marks
    .map((m) => {
      const on = new Set(m.days);
      return { ...m, with: rated.filter((c) => on.has(c.day)), without: rated.filter((c) => !on.has(c.day)) };
    })
    .filter((m) => m.with.length > 0)
    // By how many there are, never by effect.
    .sort((a, b) => b.with.length - a.with.length || a.label.localeCompare(b.label));
  if (ledger.length === 0) {
    return (
      <figure className="chart-frame plate">
        <figcaption>{since === null ? 'No day marks yet. Mark a day from Today and it is drawn here.' : 'No marked day in this window.'} <span className="n">0 marks</span>.</figcaption>
      </figure>
    );
  }
  const block = 44;
  const w = 343, h = 22 + ledger.length * block;
  return (
    <figure className="chart-frame plate">
      <svg className="chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`For each day mark, the marked days and the unmarked days drawn as points on the same scale`}>
        {Array.from({ length: 11 }, (_, i) => i - 5).map((m) => (
          <g key={m}>
            <line x1={scaleX(m)} x2={scaleX(m)} y1={18} y2={h - 2} className={m === 0 ? 'rec-axis' : 'rec-grid'} />
            {(m === -5 || m === 0 || m === 5) && <text x={scaleX(m)} y={12} className="rec-ax" textAnchor="middle">{m === 0 ? '0' : signedFigure(m)}</text>}
          </g>
        ))}
        {ledger.map((m, i) => {
          const y0 = 22 + i * block;
          return (
            <g key={m.kind}>
              <text x={0} y={y0 + 10} className="rec-lbl">{m.label}</text>
              <text x={0} y={y0 + 23} className="rec-ax">with · {m.with.length}</text>
              <text x={0} y={y0 + 37} className="rec-ax">without · {m.without.length}</text>
              {m.with.map((c) => (
                <circle key={c.day} cx={scaleX(c.end!) + jitter(c.day)} cy={y0 + 20} r={4} className="pt pt-with"><title>{`${longDay(c.day)} ${signedFigure(c.end!)}`}</title></circle>
              ))}
              {m.without.map((c) => (
                <circle key={c.day} cx={scaleX(c.end!) + jitter(c.day)} cy={y0 + 34} r={4} className="pt pt-without"><title>{`${longDay(c.day)} ${signedFigure(c.end!)}`}</title></circle>
              ))}
            </g>
          );
        })}
      </svg>
      <figcaption>
        For each mark, the days that carried it and the days that did not, as points on one scale, ordered by how many there are and never by effect. No bar, no verdict, no “your worst days”. Two clouds that sit on top of each other say so themselves, and they will separate — if they ever do — long before any statistic says it.{' '}
        <span className="n">{since ? `since ${shortDay(since)}, when you first used them · ` : ''}{rated.length} rated days</span>.
      </figcaption>
    </figure>
  );
}

/* --- What the record can say yet ----------------------------------------- */

export interface CanSayRow {
  question: string;
  have: string;
  today: 'Drawn' | 'Points only' | 'Not yet' | 'Not captured';
  changes: string;
}

export function canSayRows(cells: DayCell[], p: TrendPoints, beliefPairs: number, worksheets: number, inside: number): CanSayRow[] {
  const rated = cells.filter((c) => c.end !== null).length;
  const rows: CanSayRow[] = [
    { question: 'Where each day ended', have: `${rated} of ${cells.length} days`, today: 'Drawn', changes: 'Nothing. Fills in as you use it.' },
    { question: 'When in the day you check in', have: `${p.checkins.length} check-ins`, today: p.checkins.length >= 20 ? 'Drawn' : 'Not yet', changes: p.checkins.length >= 20 ? 'Nothing.' : `${20 - p.checkins.length} more check-ins.` },
    { question: 'Whether a day moves while it runs', have: `${inside} days with 2+`, today: inside > 0 ? 'Drawn' : 'Not yet', changes: 'A second check-in on more days.' },
  ];
  const ratedSince = cells.filter((c) => c.end !== null && (p.marksSince === null || c.day >= p.marksSince));
  for (const m of p.marks) {
    const on = new Set(m.days);
    const w = ratedSince.filter((c) => on.has(c.day)).length;
    const wo = ratedSince.length - w;
    rows.push({
      question: `${m.label} days against the rest`,
      have: `${w} with · ${wo} without`,
      today: w >= 5 && wo >= 5 ? 'Points only' : 'Not yet',
      changes: w >= 5 && wo >= 5 ? 'About 30 of each before a difference can be told from noise.' : `Five rated days on each side before it is drawn${p.marksSince ? `; marks began ${shortDay(p.marksSince)}` : ''}.`,
    });
  }
  const r = p.ratings;
  rows.push({ question: 'Energy · Overwhelm · Restlessness · Anxiety, across the day', have: `${r.energy} · ${r.overwhelm} · ${r.restlessness} · ${r.anxiety} of ${p.checkins.length} check-ins`, today: 'Not yet', changes: 'The rotating extra rating on each check-in, about one in four, until each has thirty.' });
  rows.push({ question: 'Help now, before and after', have: `${p.helpNow.pairs} pairs of ${p.helpNow.visits} visits`, today: p.helpNow.pairs > 0 ? 'Points only' : 'Not captured', changes: 'The "how bad" tap on the way in, and the one on the way out. The strongest measurement this app can make.' });
  rows.push({ question: 'Belief before and after a worksheet', have: `${beliefPairs} pairs of ${worksheets}`, today: beliefPairs > 0 ? 'Drawn' : 'Not yet', changes: beliefPairs > 0 ? 'Nothing.' : 'A worksheet finished with both ratings.' });
  rows.push({ question: 'Sleep', have: p.sleepDays === 0 ? 'Not said yet' : `${p.sleepDays} ${p.sleepDays === 1 ? 'night' : 'nights'}`, today: p.sleepDays >= 5 ? 'Points only' : 'Not yet', changes: 'The three chips on the morning face: short, okay, long. Not a score.' });
  return rows;
}

export function CanSayTable({ rows }: { rows: CanSayRow[] }): JSX.Element {
  return (
    <div className="cansay">
      <table>
        <thead>
          <tr><th scope="col">Question</th><th scope="col">Have</th><th scope="col">Today</th><th scope="col">What changes it</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.question}>
              <td>{r.question}</td>
              <td className="n">{r.have}</td>
              <td className={`cansay-${r.today.toLowerCase().replace(/ /g, '-')}`}>{r.today}</td>
              <td>{r.changes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --- helpers ---------------------------------------------------------------- */

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}
function mondayOf(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  const back = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}
/** A small, stable horizontal offset so two days at the same figure do not hide each other. */
function jitter(day: string): number {
  let h = 0;
  for (const ch of day) h = (h * 31 + ch.charCodeAt(0)) % 1000;
  return (h % 9) - 4;
}
export function shortDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
export function longDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}
